import React from 'react'

import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import styled from 'styled-components'

import { verticalLandingPageData } from 'src/data/verticalLandingPages'
import { FeatureShowcase } from 'src/components/blocks/FeatureShowcase'
import Footer from 'src/modules/organisms/footer/Footer'
import { GuidedBlocks } from 'src/components/blocks/GuidedBlocks'
import { LogostripAlt } from 'src/components/blocks/Logostrip'
import Navbar from 'src/modules/organisms/navbar/Navbar'
import { QuoteCardNew } from 'src/components/blocks/QuoteCard'
import { TwoThirdsCopyCards } from 'src/components/blocks/TwoThirdsCopyCards'
import {
  TitleH2,
  TitleH3,
  Body2Regular,
  Body3,
  Display
} from 'src/components/blocks/Typography'
import { VideoContentBlock } from 'src/components/blocks/VideoContentBlock'
import { WayFinder } from 'src/components/blocks/WayFinder'
import { BrandButton } from 'src/components/partials/Button'
import { Container } from 'src/components/partials/Container'
import ContentStack from 'src/components/partials/ContentStack'
import { Grid, Column } from 'src/components/partials/Grid'
import Layout from 'src/components/partials/Layout'
import { ResponsiveSectionWrapper } from 'src/components/partials/Section'
import SectionHeader from 'src/components/partials/SectionHeader'
import SEO from 'src/components/partials/Seo'
import {
  wayfinderDataMapping,
  wayfinderDataPortal,
  wayfinderDataWorkbooks
} from 'src/data/wayfinderData'
import { Breakpoints } from 'src/utilities/Breakpoints'
import { BrandColors, BrandTokens } from 'src/utilities/Colors'
import { FontFamily } from 'src/utilities/Type'

const HeaderSectionWrapper = styled(ResponsiveSectionWrapper)`
  ${Display} {
    max-width: 665px;
    margin: 0 auto 2rem;
  }

  .summary {
    color: ${BrandColors.black};

    h2 {
      font-size: 1rem;
      color: ${BrandColors.black};
      margin-block: 1rem;
    }

    p {
      margin: 2rem 0 1rem;
      max-width: 45ch;
      letter-spacing: normal;
      font-size: 1.125rem;
      line-height: 1.43;
      color: ${BrandColors.black};
      font-weight: 400;
    }

    ul {
      margin-top: 1rem;
      list-style: disc;
      margin-left: 1rem;
      margin-bottom: 2rem;
      font-size: 1rem;
      line-height: 1.43;
      color: ${BrandColors.black};
      max-width: 665px;

      li p {
        margin: 0 0 0.25rem;
        font-size: 1rem;
      }
    }
  }

  em {
    color: ${BrandColors.nectar900};
    font-family: ${FontFamily.brandPrimary};
    font-style: inherit;
    font-weight: 700;
  }

  .contentWrap {
    max-width: 620px;
    margin-inline: auto;
  }
`

const ButtonsWrapper = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 2rem;
  flex-direction: column;
  align-items: center;

  @media (min-width: ${Breakpoints.md}px) {
    flex-direction: row;
  }
`

const ImgContainer = styled.div`
  width: 100%;
  max-width: 1643px;
  padding-inline: 1rem;
  text-align: center;
  margin: 4rem auto 0;
  /* transform: translateX(-50%); */
  position: relative;
  /* left: 50%; */

  /* @media (min-width: ${Breakpoints.md}px) {
    margin-top: 0;
  } */
`

const HeaderSection = ({ data }) => {
  return (
    <HeaderSectionWrapper
      backgroundColor={BrandTokens.backgroundLight}
      paddingLarge='10rem 0 5rem'
      paddingSmall='8rem 0 3rem'
    >
      <Container>
        <SectionHeader
          titleContent={data.heading || null}
          titleAs='h2'
          marginBottom='1rem'
          bodyContent={data.bodyText?.bodyText || null}
          topHatText={data?.topHatText || null}
          sectionMaxWidth='120ch'
          titleMaxWidth='120ch'
          bodyMaxWidth='85ch'
        />

        {data.ctaButtons && (
          <ButtonsWrapper>
            {/* set inverted and role='secondary' on second button */}
            {data.ctaButtons.map((button, index) => (
              <div key={index}>
                <BrandButton
                  to={button.buttonUrl}
                  // external=
                  role={index === 1 ? 'secondary' : ''}
                  color={index === 1 ? BrandColors.primary : BrandColors.white}
                  inverted={index === 1}
                  label={button.buttonText}
                />
              </div>
            ))}
          </ButtonsWrapper>
        )}
      </Container>

      <ImgContainer>
        <GatsbyImage
          image={getImage(data.image)}
          alt='hero image'
          className='comilanceIllustration'
          width={1577}
        />
      </ImgContainer>
    </HeaderSectionWrapper>
  )
}

const SideBySideContentWrapper = styled(ResponsiveSectionWrapper)``

const Flex = styled.div`
  display: flex;
  gap: 2rem;
  justify-content: space-between;
  align-items: center;
  /* flex-direction: column; */
  margin-bottom: 4rem;
  flex-direction: column-reverse;

  &:last-child {
    margin-bottom: 0;
  }

  &.swapOrder {
    @media (min-width: ${Breakpoints.md}px) {
      flex-direction: row-reverse;
    }
  }

  @media (min-width: ${Breakpoints.md}px) {
    margin-bottom: 10rem;
    flex-direction: row;
    gap: 4rem;
  }
`

const ContentWrapper = styled.div`
  width: 50%;
  /* flex: 1; */
  @media (max-width: ${Breakpoints.md}px) {
    width: 100%;
  }
`

const ImageContainer = styled.div`
  /* width: 100%; */
  width: 50%;
  text-align: center;

  @media (max-width: ${Breakpoints.md}px) {
    width: 100%;
  }
`

const SideBySideContent = ({ data, headerData }) => (
  <SideBySideContentWrapper paddingLarge='5rem 0' paddingSmall='4rem 0'>
    <Container>
      {headerData && (
        <SectionHeader
          titleContent={headerData.heading}
          bodyContent={headerData.bodyText?.bodyText}
          titleAs='h2'
          titleMaxWidth='100%'
          bodyMaxWidth='110ch'
          marginBottom='5rem'
        />
      )}

      {data.map((section, index) => (
        <Flex key={index} className={index % 2 === 0 ? '' : 'swapOrder'}>
          <ContentWrapper>
            <ContentStack
              balanceText
              title={section?.heading}
              body={
                (
                  <Body3 marginTop='1rem' textWrapBalance={true}>
                    {section?.intro?.intro}
                  </Body3>
                ) || null
              }
              checklist={section?.checklist || null}
              ctaLinkText={section?.ctaLink?.linkText || null}
              ctaLinkUrl={section?.ctaLink?.linkUrl || null}
            />
          </ContentWrapper>

          {section.image && (
            <ImageContainer>
              <img src={section?.image?.url} alt='' loading='eager' />
              {/* <GatsbyImage
                image={getImage(section?.image)}
                alt={section?.heading || ''}
              /> */}
            </ImageContainer>
          )}
        </Flex>
      ))}
    </Container>
  </SideBySideContentWrapper>
)

const FlatfileFeaturesSectionWrapper = styled(ResponsiveSectionWrapper)``

const FlatfileFeaturesSection = ({ headerData, data }) => {
  const featuresData = data.map(item => ({
    description: item.intro.intro,
    graphic: <img src={item.image.url} alt={item.heading} />,
    title: item.heading
  }))

  return (
    <FlatfileFeaturesSectionWrapper
      backgroundColor={BrandTokens.backgroundDark}
      paddingLarge='6rem 0 5rem'
      paddingSmall='3rem 0 3rem'
    >
      <Container>
        <SectionHeader
          titleContent={headerData.heading}
          bodyContent={headerData.bodyText?.bodyText}
          bodyMaxWidth='110ch'
          titleMaxWidth='100%'
          sectionMaxWidth='960px'
          marginBottom='5rem'
          titleColor='white'
          bodyColor='white'
        />
        <FeatureShowcase features={featuresData} colorTheme='dark' />
      </Container>
    </FlatfileFeaturesSectionWrapper>
  )
}

const ContentBoxesSectionWrapper = styled(ResponsiveSectionWrapper)``

const ContentBox = styled.div`
  text-align: center;
  padding: 2rem;
  border: 2px solid ${BrandTokens.borderDark};
  box-shadow: ${BrandTokens.boxShadow8};
  height: 100%;
`

const ContentBoxesSection = ({ data, title }) => (
  <ContentBoxesSectionWrapper
    backgroundColor={BrandTokens.backgroundLight}
    paddingLarge='5rem 0'
    paddingSmall='4rem 0'
  >
    <Container>
      {title && (
        <TitleH2 align='center' marginBottom='3rem'>
          {title}
        </TitleH2>
      )}
    </Container>

    <Grid
      container
      columns={{
        md: 2
      }}
      spacing={{
        md: 3
      }}
    >
      {/* show data dump on page */}
      {data.map((box, index) => (
        <Column key={index}>
          <ContentBox>
            {box.title && <TitleH3 marginBottom='1rem'>{box.title}</TitleH3>}
            {box.intro.intro && (
              <Body2Regular textWrapBalance>{box.intro.intro}</Body2Regular>
            )}
          </ContentBox>
        </Column>
      ))}
    </Grid>
  </ContentBoxesSectionWrapper>
)

const GatedContentBoxWrapper = styled(ResponsiveSectionWrapper)``

const GatedContentCard = styled(Container)`
  box-sizing: border-box;
  border: 2px solid ${BrandTokens.borderDark};
  padding: 3rem;
  box-shadow: ${BrandTokens.boxShadowOutline};
  max-width: 1000px;

  img {
    max-height: 360px;
    display: block;
    margin: 0 auto;

    @media (min-width: ${Breakpoints.md}px) {
      margin-left: auto;
      margin-right: 0;
    }
  }
`

const GatedContentBox = ({ data }) => (
  <GatedContentBoxWrapper
    backgroundColor={BrandTokens.backgroundLight}
    paddingLarge='6rem 0 4rem'
    paddingSmall='4rem 0 2rem'
  >
    <GatedContentCard>
      <Grid columns={{ md: 8 }}>
        <Column span={{ md: 5 }}>
          {data.heading && (
            <TitleH2 textWrapBalance marginBottom='2rem'>
              {data.heading}
            </TitleH2>
          )}
          {data.intro && (
            <Body2Regular textWrapBalance marginBottom='2rem'>
              {data.intro?.intro}
            </Body2Regular>
          )}
          {data.ctaButton && (
            <BrandButton
              to={data.ctaButton[0].buttonUrl}
              role='primary'
              color={BrandColors.white}
              label={data.ctaButton[0].buttonText}
            />
          )}
        </Column>

        <Column span={{ md: 3 }}>
          {data.image && (
            <GatsbyImage
              image={getImage(data.image)}
              alt={data?.heading || ''}
            />
          )}
        </Column>
      </Grid>
    </GatedContentCard>
  </GatedContentBoxWrapper>
)

// Helper function to transform nested Contentful API data to flattened structure
const transformPageData = (rawData: any) => {
  if (!rawData) return null

  // Helper to get nested fields or direct value
  const getField = (obj: any, key: string) => obj?.fields?.[key] ?? obj?.[key]
  const getAssetUrl = (asset: any) => asset?.fields?.file?.url ?? asset?.url

  // Transform ctaButtons array
  const transformCtaButtons = (buttons: any[]) =>
    buttons?.map(btn => ({
      buttonText: getField(btn, 'buttonText'),
      buttonUrl: getField(btn, 'buttonUrl'),
      buttonStyle: getField(btn, 'buttonStyle')
    }))

  // Transform ctaLink
  const transformCtaLink = (link: any) =>
    link
      ? {
          linkText: getField(link, 'linkText'),
          linkUrl: getField(link, 'linkUrl'),
          title: getField(link, 'title')
        }
      : null

  return {
    ...rawData,
    hidePage: rawData.hidePage,
    urlSlug: rawData.urlSlug,
    showWayfinder: rawData.showWayfinder,
    platformOverviewHeading: rawData.platformOverviewHeading,
    seo: rawData.seo
      ? {
          title: getField(rawData.seo, 'title'),
          description: getField(rawData.seo, 'description'),
          image: { url: getAssetUrl(getField(rawData.seo, 'image')) }
        }
      : null,
    hero: rawData.hero
      ? {
          heading: getField(rawData.hero, 'heading'),
          topHatText: getField(rawData.hero, 'topHatText'),
          bodyText: {
            bodyText:
              typeof getField(rawData.hero, 'bodyText') === 'string'
                ? getField(rawData.hero, 'bodyText')
                : getField(rawData.hero, 'bodyText')?.bodyText
          },
          image: { url: getAssetUrl(getField(rawData.hero, 'image')) },
          ctaButtons: transformCtaButtons(getField(rawData.hero, 'ctaButtons'))
        }
      : null,
    logoBanner: rawData.logoBanner
      ? {
          title: getField(rawData.logoBanner, 'title'),
          logos: getField(rawData.logoBanner, 'logos')?.map((logo: any) => ({
            url: getAssetUrl(logo)
          }))
        }
      : null,
    customerQuoteSection: rawData.customerQuoteSection
      ? {
          body: {
            body:
              getField(rawData.customerQuoteSection, 'body')?.body ??
              getField(rawData.customerQuoteSection, 'body')
          },
          photo: {
            url: getAssetUrl(getField(rawData.customerQuoteSection, 'photo'))
          },
          ctaLink: transformCtaLink(
            getField(rawData.customerQuoteSection, 'ctaLink')
          ),
          quotee: getField(rawData.customerQuoteSection, 'quotee')
            ? {
                fullName: getField(
                  getField(rawData.customerQuoteSection, 'quotee'),
                  'fullName'
                ),
                jobTitle: getField(
                  getField(rawData.customerQuoteSection, 'quotee'),
                  'jobTitle'
                ),
                company: getField(
                  getField(rawData.customerQuoteSection, 'quotee'),
                  'company'
                )
                  ? {
                      name: getField(
                        getField(
                          getField(rawData.customerQuoteSection, 'quotee'),
                          'company'
                        ),
                        'name'
                      ),
                      logoBlack: {
                        url: getAssetUrl(
                          getField(
                            getField(
                              getField(rawData.customerQuoteSection, 'quotee'),
                              'company'
                            ),
                            'logoBlack'
                          )
                        )
                      }
                    }
                  : null
              }
            : null
        }
      : null,
    guidedBlocksSection: rawData.guidedBlocksSection
      ? {
          title: getField(rawData.guidedBlocksSection, 'title'),
          bodyText: {
            bodyText:
              getField(rawData.guidedBlocksSection, 'bodyText')?.bodyText ??
              getField(rawData.guidedBlocksSection, 'bodyText')
          },
          cards: getField(rawData.guidedBlocksSection, 'cards')?.map(
            (card: any) => ({
              title: getField(card, 'title'),
              bodyText: {
                bodyText:
                  getField(card, 'bodyText')?.bodyText ??
                  getField(card, 'bodyText')
              },
              linkText: getField(card, 'linkText'),
              linkUrl: getField(card, 'linkUrl')
            })
          )
        }
      : null,
    platformOverviewFeatures: rawData.platformOverviewFeatures?.map(
      (feature: any) => ({
        title: getField(feature, 'title'),
        intro: {
          intro: getField(feature, 'intro')?.intro ?? getField(feature, 'intro')
        }
      })
    ),
    gatedContentBox: rawData.gatedContentBox
      ? {
          heading: getField(rawData.gatedContentBox, 'heading'),
          intro: {
            intro:
              getField(rawData.gatedContentBox, 'intro')?.intro ??
              getField(rawData.gatedContentBox, 'intro')
          },
          ctaButton: transformCtaButtons(
            getField(rawData.gatedContentBox, 'ctaButton')
          ),
          image: {
            url: getAssetUrl(getField(rawData.gatedContentBox, 'image'))
          }
        }
      : null,
    timedCarouselHeader: rawData.timedCarouselHeader
      ? {
          heading: getField(rawData.timedCarouselHeader, 'heading'),
          bodyText: {
            bodyText:
              getField(rawData.timedCarouselHeader, 'bodyText')?.bodyText ??
              getField(rawData.timedCarouselHeader, 'bodyText')
          }
        }
      : null,
    timedCarouselItems: rawData.timedCarouselItems?.map((item: any) => ({
      heading: getField(item, 'heading'),
      intro: {
        intro: getField(item, 'intro')?.intro ?? getField(item, 'intro')
      },
      image: { url: getAssetUrl(getField(item, 'image')) }
    })),
    twoThirdsSection: rawData.twoThirdsSection
      ? {
          heading: getField(rawData.twoThirdsSection, 'heading'),
          topHatText: getField(rawData.twoThirdsSection, 'topHatText'),
          title: getField(rawData.twoThirdsSection, 'title'),
          checklist: getField(rawData.twoThirdsSection, 'checklist'),
          ctaButton: transformCtaButtons(
            getField(rawData.twoThirdsSection, 'ctaButton')
          )
        }
      : null,
    waterfallSectionHeader: rawData.waterfallSectionHeader
      ? {
          heading: getField(rawData.waterfallSectionHeader, 'heading'),
          bodyText: {
            bodyText:
              getField(rawData.waterfallSectionHeader, 'bodyText')?.bodyText ??
              getField(rawData.waterfallSectionHeader, 'bodyText')
          }
        }
      : null,
    waterfallSectionItems: rawData.waterfallSectionItems?.map((item: any) => ({
      heading: getField(item, 'heading'),
      title: getField(item, 'title'),
      topHatText: getField(item, 'topHatText'),
      checklist: getField(item, 'checklist'),
      intro: {
        intro: getField(item, 'intro')?.intro ?? getField(item, 'intro')
      },
      image: { url: getAssetUrl(getField(item, 'image')) },
      ctaLink: transformCtaLink(getField(item, 'ctaLink'))
    })),
    webinarSection: rawData.webinarSection
      ? {
          heading: getField(rawData.webinarSection, 'heading'),
          topHatText: getField(rawData.webinarSection, 'topHatText'),
          intro: {
            intro:
              getField(rawData.webinarSection, 'intro')?.intro ??
              getField(rawData.webinarSection, 'intro')
          },
          ctaButton: transformCtaButtons(
            getField(rawData.webinarSection, 'ctaButton')
          ),
          video: getField(rawData.webinarSection, 'video')
            ? {
                video: {
                  items:
                    getField(getField(rawData.webinarSection, 'video'), 'video')
                      ?.items ?? []
                }
              }
            : null
        }
      : null
  }
}

const VerticalLandingPageTemplate = ({ pageContext }) => {
  // Find the page by urlSlug from local data
  const rawPageData = verticalLandingPageData.find(
    page => page.urlSlug === pageContext.urlSlug
  )

  if (!rawPageData) {
    return null
  }

  const pageData = transformPageData(rawPageData)

  const activeQuote = {
    quote: {
      author: {
        name: pageData.customerQuoteSection?.quotee?.fullName,
        title: pageData.customerQuoteSection?.quotee?.jobTitle
      },
      body: pageData.customerQuoteSection?.body?.body,
      company: {
        jewelLogoSrc: '',
        logoSrc: '',
        logoSrcBlack:
          pageData.customerQuoteSection?.quotee?.company?.logoBlack?.url,
        name: pageData.customerQuoteSection?.quotee?.company?.name,
        photoSrc: pageData.customerQuoteSection?.photo?.url
      }
    },
    cta: {
      text: pageData.customerQuoteSection?.ctaLink?.linkText,
      url: pageData.customerQuoteSection?.ctaLink?.linkUrl
    }
  }

  const GuidedBlocksData = pageData.guidedBlocksSection?.cards.map(card => ({
    body: card.bodyText?.bodyText,
    linkLabel: card.linkText,
    linkPath: card.linkUrl,
    title: card.title
  }))

  const GuidedBlocksHeaderData = {
    body: pageData.guidedBlocksSection?.bodyText?.bodyText || '',
    title: pageData.guidedBlocksSection?.title || ''
  }

  // make pageData.twoThirdsSection.checklist match TwoThirdsCopyCardsData
  const TwoThirdsCopyCardsData = pageData.twoThirdsSection?.checklist.map(
    item => ({
      title: item
    })
  )

  return (
    <Layout>
      <Navbar />

      <HeaderSection data={pageData.hero} />

      {pageData.logoBanner && (
        <LogostripAlt
          showTopHat
          topHatText={pageData.logoBanner.title}
          logos={pageData.logoBanner.logos}
        />
      )}

      {pageData.twoThirdsSection && (
        <TwoThirdsCopyCards
          headerData={{
            title: pageData.twoThirdsSection?.heading,
            topHat: pageData.twoThirdsSection?.topHatText
          }}
          cardData={TwoThirdsCopyCardsData}
          cta={{
            label: pageData.twoThirdsSection?.ctaButton
              ? pageData.twoThirdsSection?.ctaButton[0]?.buttonText
              : '',
            path: pageData.twoThirdsSection?.ctaButton
              ? pageData.twoThirdsSection?.ctaButton[0]?.buttonUrl
              : ''
          }}
        />
      )}

      {pageData.timedCarouselItems && (
        <FlatfileFeaturesSection
          headerData={pageData.timedCarouselHeader}
          data={pageData.timedCarouselItems}
        />
      )}

      {pageData.gatedContentBox && (
        <GatedContentBox data={pageData.gatedContentBox} />
      )}

      {pageData.waterfallSectionItems && (
        <SideBySideContent
          data={pageData.waterfallSectionItems}
          headerData={pageData.waterfallSectionHeader}
        />
      )}

      {/* webinar module */}
      {pageData.webinarSection && pageData.webinarSection.video && (
        <VideoContentBlock
          videoId={pageData.webinarSection.video?.video?.items[0]?.hashed_id}
          title={pageData.webinarSection?.heading}
          body={pageData.webinarSection?.intro?.intro}
          ctaText={
            pageData.webinarSection?.ctaButton
              ? pageData.webinarSection?.ctaButton[0]?.buttonText
              : ''
          }
          ctaUrl={
            pageData.webinarSection?.ctaButton
              ? pageData.webinarSection?.ctaButton[0]?.buttonUrl
              : ''
          }
        />
      )}

      {GuidedBlocksData && (
        <GuidedBlocks
          theme='dark'
          headerData={GuidedBlocksHeaderData}
          cardData={GuidedBlocksData}
        />
      )}

      {pageData.platformOverviewFeatures && (
        <ContentBoxesSection
          data={pageData.platformOverviewFeatures}
          title={pageData.platformOverviewHeading}
        />
      )}

      {pageData.customerQuoteSection && (
        <QuoteCardNew
          quote={activeQuote.quote}
          cta={{
            text: activeQuote.cta.text,
            url: activeQuote.cta.url
          }}
        />
      )}

      {pageData.showWayfinder && (
        <WayFinder
          cardData={[
            wayfinderDataMapping,
            wayfinderDataPortal,
            wayfinderDataWorkbooks
          ]}
        />
      )}

      <Footer variant='secondary' showCta={false} />
    </Layout>
  )
}

export default VerticalLandingPageTemplate

export const Head = ({ pageContext }) => {
  const rawPageData = verticalLandingPageData.find(
    page => page.urlSlug === pageContext.urlSlug
  )
  const pageData = transformPageData(rawPageData)

  return (
    <SEO
      description={
        pageData?.seo?.description ||
        pageData?.hero?.bodyText?.bodyText ||
        'Build the ideal data file import experience with Flatfile and turn csv, xls, xlsx, pdf and more into API-ready data. See how it works or start for free now.'
      }
      title={
        pageData?.seo?.title ||
        pageData?.hero?.heading ||
        'Smart APIs for file-based data import'
      }
      image={
        pageData?.seo?.image?.url ||
        'https://flatfile.com/img/og/flatfile-ai-powered-data-migration-platform-og-img4.png'
      }
      robots={pageData?.hidePage ? 'hide' : null}
    />
  )
}
